/**
 * NetworkAddressInputForm — "add an address" form shared by every
 * per-chain monitor.
 *
 * One-line text input with live-validation indicator (✓ / ✗ on the
 * right edge of the input) + a submit button styled with the chain
 * gradient. Submission also fires on Enter.
 *
 * Validation is delegated to the parent: this component just
 * displays the validity state passed via `isValid`.
 */

import type { Dispatch, SetStateAction } from 'react';
import { Icons } from '../Icons';
import type { NetworkColors } from './types';

interface NetworkAddressInputFormProps {
  /** Current input value (controlled). */
  value: string;
  /** Setter for the input value. */
  onChange: Dispatch<SetStateAction<string>>;
  /** Called when the user clicks Submit or presses Enter. */
  onSubmit: () => void;
  /** Submit button label (i18n in caller). */
  submitLabel: string;
  /** Placeholder text inside the input. */
  placeholder: string;
  /** Validation state computed by the caller. Drives the trailing icon
   *  + the submit button enabled-state. `null` = empty input, no icon. */
  isValid: boolean | null;
  /** Per-chain colour palette. */
  colors: NetworkColors;
  /** Optional aria-label override. Defaults to the placeholder. */
  inputAriaLabel?: string;
  /** Optional small helper text rendered below the input + button row.
   *  Common use: "Enter your Axelar address starting with axelar1". */
  helperText?: string;
}

export function NetworkAddressInputForm({
  value,
  onChange,
  onSubmit,
  submitLabel,
  placeholder,
  isValid,
  colors,
  inputAriaLabel,
  helperText,
}: NetworkAddressInputFormProps) {
  const successColor = colors.success ?? '#10b981';
  const submitEnabled = isValid === true;

  return (
    <div
      style={{
        background: 'rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(0, 0, 0, 0.3)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <div style={{ display: 'flex', gap: '12px' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSubmit();
            }}
            placeholder={placeholder}
            aria-label={inputAriaLabel ?? placeholder}
            autoFocus
            style={{
              width: '100%',
              padding: '12px 14px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: `1px solid ${
                value && isValid === false ? '#ef4444' : 'rgba(255, 255, 255, 0.15)'
              }`,
              borderRadius: '10px',
              color: 'var(--text)',
              fontSize: '13px',
              fontFamily: 'monospace',
              outline: 'none',
            }}
          />
          {value && isValid !== null && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: isValid ? successColor : '#ef4444',
              }}
            >
              {isValid ? <Icons.Check size={16} /> : <Icons.X size={16} />}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!submitEnabled}
          style={{
            padding: '12px 20px',
            background: colors.gradient,
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            opacity: submitEnabled ? 1 : 0.5,
            cursor: submitEnabled ? 'pointer' : 'not-allowed',
          }}
        >
          <Icons.Check size={16} />
          {submitLabel}
        </button>
      </div>
      {helperText && (
        <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '12px' }}>
          {helperText}
        </p>
      )}
    </div>
  );
}
